'use client';
import { Footer } from '@chemist4u/newt-components';
import { trackNewsletterSignup } from '@lib/analytics/events';
import { brand, storeName } from '@lib/config/brand';
import { subscribeToNewsletter } from '@lib/data/newsletter';
import { composeThemeToken } from '@lib/mappers';
import { useAuth } from '@modules/common/components/auth-provider';
import { useState } from 'react';
import {
  ACCREDITATION_LOGOS,
  FOOTER_BOTTOM_LINKS,
  FOOTER_HELP_LINKS,
  FOOTER_LEGAL_LINKS,
  type FooterLink,
  NEWSLETTER,
  PAYMENT_METHODS,
} from './footer-data';
import { useFooterCookieListener } from './use-footer-cookie-listener';
import { useFooterEmailListener } from './use-footer-email-listener';

function mapFooterLink(link: FooterLink) {
  const { href, action, isExternal } = link;
  let mappedHref = href || '#';

  if (action) {
    mappedHref = `#${action}`;
  }

  return {
    label: link.label,
    href: mappedHref,
    isExternal,
  };
}

export default function FooterComp() {
  const { structuredAddress, logo, telephone, gphcReg } = brand;
  const { isLoggedIn, showNewsletterSignup } = useAuth();
  useFooterCookieListener();
  useFooterEmailListener();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  async function handleNewsletterSubmit(email: string) {
    setIsSubmitting(true);
    setErrorMessage('');

    const result = await subscribeToNewsletter(email);

    setIsSubmitting(false);

    if (result.success) {
      trackNewsletterSignup('footer', email);
      setIsSuccess(true);
    } else {
      setErrorMessage('Something went wrong. Please try again.');
    }
  }

  const rightsReserved = `All rights reserved. Registered and regulated UK pharmacy with the GPhC (registered premises ${gphcReg}).`;

  return (
    <div data-theme={composeThemeToken(storeName, 'primary-strong-muted')}>
      <Footer
        logo={{
          href: '/',
          fullLogo: {
            light: logo,
            dark: logo,
          },
        }}
        secondaryCtas={[
          {
            href: '/account',
            children: isLoggedIn ? 'My Account' : 'Sign in/Register',
            variant: 'secondary',
            iconLeft: { name: 'account', size: 'small' },
          },
        ]}
        paymentIcons={PAYMENT_METHODS}
        newsletterSignup={
          showNewsletterSignup
            ? {
                ...NEWSLETTER,
                isSubmitting,
                isSuccess,
                errorMessage,
                onSubmit: handleNewsletterSubmit,
              }
            : undefined
        }
        newsletterTheme={composeThemeToken(storeName, 'neutral-default')}
        links={[]}
        linkGroups={[
          {
            title: 'Help & Info',
            links: FOOTER_HELP_LINKS.map(mapFooterLink),
          },
          {
            title: 'Legal',
            links: FOOTER_LEGAL_LINKS.map(mapFooterLink),
          },
        ]}
        labels={{
          rightsReserved,
        }}
        policyLinks={FOOTER_BOTTOM_LINKS.map(mapFooterLink)}
        socialLinks={brand.socialLinks}
        organisationName={brand.name}
        contactDetails={{
          address: {
            addressLine1: structuredAddress.streetAddress,
            addressLine2: structuredAddress.addressLocality,
            city: structuredAddress.addressRegion,
            postcode: structuredAddress.postalCode,
          },
          telephone: telephone || '',
        }}
        accreditationLogos={ACCREDITATION_LOGOS}
        copyrightYear={new Date().getFullYear()}
      />
    </div>
  );
}
